import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  hint?: string
  prefix?: ReactNode
  suffix?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3 py-2 text-sm',
  lg: 'px-4 py-3 text-base',
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, prefix, suffix, size = 'md', className = '', id, disabled, required, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className={`flex flex-col gap-1 ${className}`}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-700 dark:text-white/65">
            {label}
            {required && (
              <>
                <span className="ml-0.5 text-red-500" aria-hidden="true">
                  *
                </span>
                <span className="sr-only"> (required)</span>
              </>
            )}
          </label>
        )}
        <div className="relative flex items-center">
          {prefix && (
            <span className="absolute inset-y-0 left-3 flex items-center text-gray-500 dark:text-white/45">
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            className={`
              w-full rounded-md border bg-white transition-colors
              dark:bg-white/5 dark:text-white dark:placeholder-white/30
              focus:outline-none focus:ring-2 focus:ring-offset-0 dark:focus:ring-offset-gray-900
              disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-white/10 dark:disabled:text-white/30
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-white/10 focus:border-primary-500 focus:ring-primary-500'}
              ${sizeClasses[size]}
              ${prefix ? 'pl-10' : ''}
              ${suffix ? 'pr-10' : ''}
            `}
            aria-invalid={error ? 'true' : undefined}
            aria-required={required || undefined}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            {...props}
          />
          {suffix && (
            <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 dark:text-white/45">
              {suffix}
            </span>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="text-sm text-gray-500 dark:text-white/45">
            {hint}
          </p>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'
